import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { samplePages } from "../data/landing";
export function SampleCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const goTo = (i: number) => {
    const track = trackRef.current;
    const child = track?.children[i] as HTMLElement | undefined;
    if (track && child) track.scrollTo({
      left: child.offsetLeft,
      behavior: "smooth"
    });
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const children = Array.from(track.children) as HTMLElement[];
    let nearest = 0;
    children.forEach((c, i) => {
      if (Math.abs(c.offsetLeft - track.scrollLeft) < Math.abs(children[nearest].offsetLeft - track.scrollLeft)) {
        nearest = i;
      }
    });
    setIndex(nearest);
  };
  useEffect(() => {
    if (zoomed === null) return;
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setZoomed(null);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [zoomed]);
  const last = samplePages.length - 1;
  const arrowClass = "flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-navy transition-colors duration-150 ease-out hover:border-teal hover:text-teal disabled:opacity-40 disabled:hover:border-line disabled:hover:text-navy";
  return <section id="preview" className="bg-soft py-16 md:py-24" data-id="element-109" data-mp="mab2uvxgrr5l">
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8" data-id="element-110" data-mp="2o0ibtyoajtr9">
        <SectionHead eyebrow="Look inside" title="See how a session" highlight="comes together." lead="A selection of pages from the playbook. Swipe to browse and tap any page to read it full size." data-id="element-111" data-mp="2o237jrmcdiqp" />

        <div ref={trackRef} onScroll={handleScroll} className="no-scrollbar relative flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2" aria-roledescription="carousel" aria-label="Playbook sample pages" data-id="element-112" data-mp="2aa6ffnkob8ub">
          {samplePages.map((page, i) => <figure key={page.title} className="w-[78%] shrink-0 snap-start sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]" aria-label={`${i + 1} of ${samplePages.length}: ${page.title}`} data-id="element-113" data-mp="2xxwpxrmqs6ge" data-mpi={i}>
              <button type="button" onClick={() => setZoomed(i)} className="group relative block w-full overflow-hidden rounded-lg border border-line bg-white shadow-[0_16px_36px_rgba(19,34,56,0.12)]" aria-label={`Open ${page.title} full size`} data-id="element-114" data-mp="1mhg5fjwbyzmo">
                <img src={page.image} alt={`${page.title} sample page`} loading="lazy" className="aspect-[9/16] w-full object-cover object-top" data-id="element-115" data-mp="63lqzp8pqaym" />
                <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-navy/90 px-3 py-1.5 text-xs font-semibold text-white transition-opacity duration-150 ease-out sm:opacity-0 sm:group-hover:opacity-100" data-id="element-116" data-mp="fsde9k2tw737">
                  <ZoomInIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-117" data-mp="ppi3jsrz0io8" />
                  Read
                </span>
              </button>
              <figcaption className="mt-4 font-display text-xl font-bold uppercase text-navy" data-id="element-118" data-mp="2rjhfmgcx341z">{page.title}</figcaption>
            </figure>)}
        </div>

        <div className="mt-8 flex items-center justify-center gap-5" data-id="element-119" data-mp="20a4h4shly0mn">
          <button type="button" onClick={() => goTo(Math.max(0, index - 1))} disabled={index === 0} aria-label="Previous sample" className={arrowClass} data-id="element-120" data-mp="208b9b9v9dad2">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="2a8tzsnhbvp3y" />
          </button>
          <div className="flex gap-2" data-id="element-122" data-mp="259h55gk7tdqt">
            {samplePages.map((page, i) => <button key={page.title} type="button" onClick={() => goTo(i)} aria-label={`Go to sample ${i + 1}`} aria-current={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === index ? "w-6 bg-teal" : "w-2 bg-navy/20 hover:bg-navy/40"}`} data-id="element-123" data-mp="1hjmriws2nl5i" data-mpi={i} />)}
          </div>
          <button type="button" onClick={() => goTo(Math.min(last, index + 1))} disabled={index === last} aria-label="Next sample" className={arrowClass} data-id="element-124" data-mp="yrao06pomdts">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-125" data-mp="35h5npfp690ja" />
          </button>
        </div>
        <p className="mt-6 text-center text-xs text-muted" data-id="element-126" data-mp="3a8e2kr99te3u">Selection only. The full playbook is not shown on this page.</p>
      </div>

      <AnimatePresence data-id="element-127" data-mp="2wik9fwqqg7ob">
        {zoomed !== null && <motion.div role="dialog" aria-modal="true" aria-label={samplePages[zoomed].title} className="fixed inset-0 z-50 overflow-y-auto bg-navy/90 p-4 sm:p-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={() => setZoomed(null)} data-id="element-128" data-mp="30hjd7e0q9ye6">
            <button ref={closeRef} type="button" onClick={() => setZoomed(null)} aria-label="Close" className="fixed right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-navy shadow-lg" data-id="element-129" data-mp="3k4h2rrkkb2o5">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-130" data-mp="ed0yjren8cc6" />
            </button>
            <motion.img src={samplePages[zoomed].image} alt={`${samplePages[zoomed].title} sample page`} className="mx-auto w-full max-w-[720px] rounded-lg bg-white" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={e => e.stopPropagation()} data-id="element-131" data-mp="9cd8b7y8nzj0" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}